<template>
  <LoadingWrapper :state="status">
    <div class="flex w-full flex-row items-center justify-between pt-6 pb-4">
      <div v-if="analysisData" class="flex flex-col -space-y-1">
        <div
          v-for="entry in analysisData.perspectives"
          v-show="entry.numberOfFeedbacks > 0"
          :key="entry.key"
          class="flex flex-row items-center"
        >
          <span class="text-body-large text-primary mr-2 inline-block w-6 text-right font-semibold">
            {{ entry.numberOfFeedbacks }}
          </span>
          <span v-if="entry.key === SELF_PERSPECTIVE_KEY" class="text-body-medium">
            {{ $t('perspectives.self-long', { count: entry.numberOfFeedbacks }) }}
          </span>
          <template v-else>
            <span class="text-body-medium">
              {{ $t('common.feedback', { count: entry.numberOfFeedbacks }) }}
              <span class="lowercase">{{ $t('common.from') }}</span>
              {{ entry.numberOfSenders }}
              {{ perspectiveLabel(entry.key, entry.numberOfSenders) }}
            </span>
            <BaseMemberAvatars
              v-if="entry.key === firstNonSelfKey && nonSelfMembers.length > 0"
              size="24"
              :members="nonSelfMembers"
              class="bg-surface pl-1"
            />
          </template>
        </div>
      </div>
      <BaseTextButton
        v-if="data.surveys.length > 0"
        icon="filter_list"
        shape="square"
        color="outlined"
        @click="openSurveySelectorDialog"
        >{{ selectedSurveyIds.length }}
        {{ $t('common.survey', { count: selectedSurveyIds.length }) }}</BaseTextButton
      >
      <span v-else class="text-label-medium text-on-surface-variant">
        {{ $t('profile.noSurveys') }}
      </span>

      <SurveySelectorDialog
        v-if="coachee"
        :subject="coachee"
        :title="`Umfragen zu ${data.title}`"
        :model-value="dialogSelectedSurveyIds"
        :is-open="isOpen"
        :all-surveys="[...data.surveys]"
        :topic-perspectives="topicPerspectives"
        @update:model-value="handleSurveySelection"
        @close-dialog="closeDialog"
      />
    </div>
    <EvaluationProfileAnalysis
      v-if="analysisData && coachee"
      :subject="coachee"
      :skills="analysisData.skills"
      :scale="analysisData.scale"
      :questions-data="analysisData.questions"
      :skills-data="analysisData.skills"
      :texts="analysisData.texts"
      :questions="analysisData?.questions"
      :topic-perspectives="topicPerspectives"
      :show-blindspots="data.showBlindspots"
      :skill-visualization="data.skillVisualization"
    />
  </LoadingWrapper>
</template>

<script setup lang="ts">
  import LoadingWrapper from '@/core/components/loading/LoadingWrapper.vue'
  import EvaluationProfileAnalysis from '@/core/components/analytics/EvaluationProfileAnalysis.vue'
  import SurveySelectorDialog from '../../../core/components/survey-selector/SurveySelectorDialog.vue'
  import { computed, ref, toRef } from 'vue'

  import BaseTextButton from '../../../core/components/base/button/text-button/BaseTextButton.vue'
  import { useCoacheeEvaluationProfile } from '../composables/use-coachee-evaluation-profile'
  import BaseMemberAvatars from '@/core/components/base/avatar/BaseMemberAvatars.vue'
  import { SELF_PERSPECTIVE_KEY, perspectivesFromTopic } from '@/core/models/perspective'
  import { resolvePerspectiveLabel } from '@/core/models/perspective-label'
  import { useI18n } from 'vue-i18n'

  interface Props {
    feedbackTopicId: string
  }

  const props = defineProps<Props>()

  const {
    data,
    status,
    isOpen,
    closeDialog,
    selectedSurveyIds,
    openDialog,
    applySelectedSurveys,
    analysisData,
    coachee,
  } = useCoacheeEvaluationProfile(toRef(props, 'feedbackTopicId'))

  const dialogSelectedSurveyIds = ref<string[]>([])
  const { t } = useI18n()

  const topicPerspectives = computed(() =>
    data.value ? perspectivesFromTopic(data.value) : [],
  )
  const openSurveySelectorDialog = () => {
    dialogSelectedSurveyIds.value = [...selectedSurveyIds.value]
    openDialog()
  }

  const handleSurveySelection = (surveyIds: string[]) => {
    dialogSelectedSurveyIds.value = [...surveyIds]
    applySelectedSurveys(surveyIds)
  }

  const nonSelfMembers = computed(() => {
    return (
      analysisData.value?.feedbackSenders.filter((sender) => sender.id !== coachee.value?.id) ?? []
    )
  })

  const firstNonSelfKey = computed(() => {
    return analysisData.value?.perspectives.find(
      (entry) => entry.key !== SELF_PERSPECTIVE_KEY && entry.numberOfFeedbacks > 0,
    )?.key
  })

  const perspectiveLabel = (key: string, count: number) =>
    resolvePerspectiveLabel(key, topicPerspectives.value, {
      kind: 'nameLong',
      count,
      selfLabel: t('perspectives.self'),
    })
</script>
